import { StackProps } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import React from 'react';

export interface OverflowState {
  canScrollStart: boolean;
  canScrollEnd: boolean;
  isHovered: boolean;
}

/**
 * Tracks whether a scrollable element can scroll in each direction along
 * its primary axis, and whether the mouse is currently hovering over it.
 * Re-evaluates on scroll, resize, and content size changes.
 *
 * When `enabled` is false the hook is a no-op: no listeners are registered,
 * no ResizeObserver is created, and the returned state is always the inert
 * default. Pass `false` when the caller does not need overflow tracking to
 * avoid paying the setup and re-render cost.
 */
export function useOverflowState(
  ref: React.RefObject<HTMLElement | null>,
  direction: StackProps['direction'],
  enabled = true,
): OverflowState {
  const [state, setState] = useState<OverflowState>({
    canScrollStart: false,
    canScrollEnd: false,
    isHovered: false,
  });

  const isColumn = direction === 'column' || direction === 'column-reverse';

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    // Return the previous state reference unchanged when the scroll availability
    // hasn't actually changed. React bails out of re-rendering when the state
    // updater returns the same reference, so resizes that don't cross a
    // scroll boundary (e.g. the container grows but content was already fully
    // visible) produce zero re-renders.
    if (isColumn) {
      setState((prev) => {
        const canScrollStart = el.scrollTop > 0;
        const canScrollEnd = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
        if (prev.canScrollStart === canScrollStart && prev.canScrollEnd === canScrollEnd) {
          return prev;
        }
        return { ...prev, canScrollStart, canScrollEnd };
      });
    } else {
      setState((prev) => {
        const canScrollStart = el.scrollLeft > 0;
        const canScrollEnd = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
        if (prev.canScrollStart === canScrollStart && prev.canScrollEnd === canScrollEnd) {
          return prev;
        }
        return { ...prev, canScrollStart, canScrollEnd };
      });
    }
  }, [ref, isColumn]);

  useEffect(() => {
    // Skip all setup when the caller doesn't need overflow tracking.
    if (!enabled) {
      return;
    }

    const el = ref.current;
    if (!el) {
      return;
    }

    // Initial measurement
    update();

    const onScroll = () => update();
    const onEnter = () => setState((prev) => ({ ...prev, isHovered: true }));
    const onLeave = () => setState((prev) => ({ ...prev, isHovered: false }));

    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);

    const observer = new ResizeObserver(() => update());
    observer.observe(el);
    // Also observe children to detect content size changes
    Array.from(el.children).forEach((child) => observer.observe(child));

    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      observer.disconnect();
    };
  }, [ref, update, enabled]);

  return state;
}
